import * as React from 'react';
import { NavigationMenu as NavigationMenuPrimitive } from '@base-ui/react/navigation-menu';
import { styled, keyframes, lighten } from '@mui/material/styles';
import { IconChevronDown } from 'src/libraries/icons';
import { Theme } from '@mui/material';

//
//

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;
// const slideInFromLeft = keyframes`from { opacity: 0; transform: translateX(-52%); } to { opacity: 1; transform: translateX(0); }`;
// const slideInFromRight = keyframes`from { opacity: 0; transform: translateX(52%); } to { opacity: 1; transform: translateX(0); }`;
// const slideOutToLeft = keyframes`from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-52%); }`;
// const slideOutToRight = keyframes`from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(52%); }`;

// Softer variants: a much smaller displacement (px, not %) so text doesn't visibly
// "run" across the popup — mostly a crossfade with just a hint of directional drift.
const slideInFromLeft = keyframes`from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); }`;
const slideInFromRight = keyframes`from { opacity: 0; transform: translateX(10px); } to { opacity: 1; transform: translateX(0); }`;
const slideOutToLeft = keyframes`from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-10px); }`;
const slideOutToRight = keyframes`from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(10px); }`;

//
//

const StyledRoot = styled(NavigationMenuPrimitive.Root)({
  position: 'relative',
  display: 'flex',
  flex: 1,
  maxWidth: 'max-content',
  alignItems: 'center',
  justifyContent: 'center',
});

function NavigationMenu({
  align = 'start',
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> &
  Pick<React.ComponentProps<typeof NavigationMenuPrimitive.Positioner>, 'align'>) {
  return (
    <StyledRoot data-slot="navigation-menu" {...props}>
      {children}
      <NavigationMenuPositioner align={align} />
    </StyledRoot>
  );
}

//
//

const StyledList = styled(NavigationMenuPrimitive.List)({
  display: 'flex',
  flex: 1,
  listStyle: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0,
  margin: 0,
  padding: 0,
});

function NavigationMenuList(props: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return <StyledList data-slot="navigation-menu-list" {...props} />;
}

//
//

const StyledItem = styled(NavigationMenuPrimitive.Item)({
  position: 'relative',
});

function NavigationMenuItem(props: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
  return <StyledItem data-slot="navigation-menu-item" {...props} />;
}

//
//

export const navigationMenuTriggerStyle = ({ theme }: { theme: Theme }) => ({
  display: 'inline-flex',
  height: theme.spacing(4.5),
  width: 'max-content',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 18,
  paddingInline: theme.spacing(1.5),
  paddingBlock: theme.spacing(0.75),
  fontFamily: theme.typography.body2.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 500,
  color: (theme.vars || theme).palette.text.primary,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.short,
  }),
  outline: 'none',
  border: 'none',
  background: 'none',
  // background: 'lightgreen',
  cursor: 'pointer',
  '&:hover, &:focus-visible': {
    // backgroundColor: (theme.vars || theme).palette.action.hover,
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
  },
  '&:disabled': {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  '&[data-popup-open], &[data-open]': {
    // backgroundColor: (theme.vars || theme).palette.action.selected,
    backgroundColor: lighten(theme.palette.primary.lighter, 0.4),
    color: theme.palette.primary.main,
  },
  '&[data-active]': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
});

const StyledTrigger = styled(NavigationMenuPrimitive.Trigger)(({ theme }) => ({
  ...navigationMenuTriggerStyle({ theme }),
  gap: theme.spacing(0.5),
}));

const StyledChevron = styled(IconChevronDown)({
  position: 'relative',
  top: 1,
  transition: 'transform 300ms',
  '[data-popup-open] &': {
    transform: 'rotate(180deg)',
  },
});

function NavigationMenuTrigger({
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
  return (
    <StyledTrigger data-slot="navigation-menu-trigger" {...props}>
      {children} <StyledChevron size={16} aria-hidden />
    </StyledTrigger>
  );
}

//
//

const StyledContent = styled(NavigationMenuPrimitive.Content)({
  height: '100%',
  width: 'auto',
  padding: 12,
  transition: 'opacity 350ms cubic-bezier(0.22, 1, 0.36, 1)',
  outline: 'none',
  '&[data-starting-style]': {
    opacity: 0,
  },
  '&[data-ending-style]': {
    opacity: 0,
  },
  '&[data-starting-style][data-activation-direction="right"]': {
    animation: `${slideInFromRight} 350ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
  '&[data-starting-style][data-activation-direction="left"]': {
    animation: `${slideInFromLeft} 350ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
  '&[data-ending-style][data-activation-direction="right"]': {
    animation: `${slideOutToRight} 350ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
  '&[data-ending-style][data-activation-direction="left"]': {
    animation: `${slideOutToLeft} 350ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },
});

function NavigationMenuContent(
  props: React.ComponentProps<typeof NavigationMenuPrimitive.Content>
) {
  return <StyledContent data-slot="navigation-menu-content" {...props} />;
}

//
//

const StyledPositioner = styled(NavigationMenuPrimitive.Positioner)(({ theme }) => ({
  isolation: 'isolate',
  zIndex: theme.zIndex.modal,
  height: 'var(--positioner-height)',
  width: 'var(--positioner-width)',
  maxWidth: 'var(--available-width)',
  transition: theme.transitions.create(['top', 'left', 'right', 'bottom'], {
    duration: 350,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  }),
  '&[data-instant]': {
    transition: 'none',
  },
}));

const StyledPopup = styled(NavigationMenuPrimitive.Popup)(({ theme }) => ({
  position: 'relative',
  height: 'var(--popup-height)',
  width: 'var(--popup-width)',
  transformOrigin: 'var(--transform-origin)',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.customShadows.card,
  outline: 'none',
  transition: theme.transitions.create(['opacity', 'transform', 'width', 'height'], {
    duration: 350,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  }),
  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.9)',
  },
}));

const StyledViewport = styled(NavigationMenuPrimitive.Viewport)({
  position: 'relative',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
});

function NavigationMenuPositioner({
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  alignOffset = 0,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Positioner>) {
  return (
    <NavigationMenuPrimitive.Portal>
      <StyledPositioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        {...props}
      >
        <StyledPopup>
          <StyledViewport />
        </StyledPopup>
      </StyledPositioner>
    </NavigationMenuPrimitive.Portal>
  );
}

//
//

const StyledLink = styled(NavigationMenuPrimitive.Link)(({ theme }) => ({
  display: 'flex',
  // alignItems: 'center',
  gap: 0,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  fontSize: theme.typography.body2.fontSize,
  color: (theme.vars || theme).palette.text.primary,
  textDecoration: 'none',
  outline: 'none',
  // width: '100%',
  cursor: 'pointer',
  // flex: 1,
  // border: '1px solid red',
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover, &:focus-visible': {
    backgroundColor: (theme.vars || theme).palette.action.hover,
  },
  '&[data-active]': {
    backgroundColor: (theme.vars || theme).palette.action.selected,
  },
  '& svg': {
    fontSize: '1rem',
  },
}));

function NavigationMenuLink({
  unstyled,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link> & {
  /** Skip the built-in link styling — use when `render` wraps an already-styled element (e.g. a Button) that would otherwise be visually overridden. */
  unstyled?: boolean;
}) {
  if (unstyled) {
    return <NavigationMenuPrimitive.Link data-slot="navigation-menu-link" {...props} />;
  }
  return <StyledLink data-slot="navigation-menu-link" {...props} />;
}

//
//

const StyledIndicatorRoot = styled(NavigationMenuPrimitive.Icon)({
  top: '100%',
  zIndex: 1,
  display: 'flex',
  height: 6,
  alignItems: 'flex-end',
  justifyContent: 'center',
  overflow: 'hidden',
  '&[data-visible="false"]': {
    animation: `${fadeOut} 200ms`,
  },
  '&[data-visible="true"]': {
    animation: `${fadeIn} 200ms`,
  },
});

const StyledIndicatorArrow = styled('div')(({ theme }) => ({
  position: 'relative',
  top: '60%',
  height: 8,
  width: 8,
  transform: 'rotate(45deg)',
  borderTopLeftRadius: 2,
  backgroundColor: (theme.vars || theme).palette.divider,
  boxShadow: theme.shadows[3],
}));

function NavigationMenuIndicator(props: React.ComponentProps<typeof NavigationMenuPrimitive.Icon>) {
  return (
    <StyledIndicatorRoot data-slot="navigation-menu-indicator" {...props}>
      <StyledIndicatorArrow />
    </StyledIndicatorRoot>
  );
}

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuPositioner,
};
